import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { cn } from 'lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'primitives/base/select'
import { buttonVariants } from 'primitives/button'
import { ScrollArea } from 'primitives/scroll-area'
import * as React from 'react'
import { DayPicker, type DropdownProps } from 'react-day-picker'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

const Calendar = ({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) => (
  <DayPicker
    showOutsideDays={showOutsideDays}
    className={cn('p-3 font-inter text-s-400 normal-case tracking-normal', className)}
    classNames={{
      months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
      month: 'space-y-4 w-full',
      caption: 'flex justify-center pt-1 relative items-center',
      caption_label: 'font-inter text-s-500 normal-case tracking-normal',
      caption_dropdowns: 'flex justify-center gap-2',
      nav: 'space-x-1 flex items-center',
      nav_button: cn(
        buttonVariants({ variant: 'outline' }),
        'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
      ),
      nav_button_previous: 'absolute -left-1',
      nav_button_next: 'absolute -right-1',
      table: 'w-full border-collapse space-y-1',
      head_row: 'flex w-full justify-between',
      head_cell:
        'text-muted-foreground rounded-md w-8 font-inter text-xs-400 normal-case tracking-normal',
      row: 'flex w-full mt-2 justify-between',
      cell: cn(
        'relative p-0 text-center text-s-400 focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
        props.mode === 'range'
          ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
          : '[&:has([aria-selected])]:rounded-md',
      ),
      day: cn(
        buttonVariants({ variant: 'ghost' }),
        'h-8 w-8 p-0 font-jetbrains text-xs-400 normal-case tracking-normal tabular-nums aria-selected:opacity-100',
      ),
      day_range_start: 'day-range-start',
      day_range_end: 'day-range-end',
      day_selected:
        'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
      day_today: 'bg-accent text-accent-foreground',
      day_outside:
        'day-outside text-muted-foreground opacity-50  aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
      day_disabled: 'text-muted-foreground opacity-50',
      day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
      day_hidden: 'invisible',
      vhidden: 'hidden',
      ...classNames,
    }}
    components={{
      Dropdown: ({ value, onChange, children }: DropdownProps) => {
        const options = React.Children.toArray(children) as React.ReactElement<
          React.HTMLProps<HTMLOptionElement>
        >[]
        const selected = options.find((child) => child.props.value === value)

        const handleChange = (newValue: string) => {
          const changeEvent = {
            target: { value: newValue },
          } as React.ChangeEvent<HTMLSelectElement>
          onChange?.(changeEvent)
        }

        return (
          <Select
            value={value?.toString()}
            onValueChange={(newValue) => {
              handleChange(newValue)
            }}
          >
            <SelectTrigger className="pr-1.5 gap-1 focus:ring-0 border-0 shadow-none font-inter text-s-400 normal-case tracking-normal">
              <SelectValue>{selected?.props?.children}</SelectValue>
            </SelectTrigger>
            <SelectContent position="popper">
              <ScrollArea className="h-80">
                {options.map((option) => (
                  <SelectItem
                    key={option.props.value?.toString()}
                    value={option.props.value?.toString() ?? ''}
                  >
                    {option.props.children}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
        )
      },
      IconLeft: () => <ChevronLeftIcon className="h-4 w-4" />,
      IconRight: () => <ChevronRightIcon className="h-4 w-4" />,
    }}
    {...props}
  />
)
Calendar.displayName = 'Calendar'

export { Calendar }
